import { useEffect } from 'react'
import { type FieldError, useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Button, ButtonGroup, Checkbox, EmailInput, PasswordInput, TextLink, usePageTitle } from 'slab'
import { handleGrapeAPIFieldErrors } from 'utils'
import { usePostEmployersRegistration } from 'utils/web_api'

import { CenteredLayout } from '../CenteredLayout'
import { usePrefill } from '../PrefillProvider'
import { useAPIErrorBanner } from '../useErrorBanner'

export function RegisterPage ({ authenticated }: { authenticated?: boolean }) {
  usePageTitle('Create your account')
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not sign up because of an error',
  })

  const { getPrefillValue } = usePrefill()

  const form = useForm({
    defaultValues: {
      email: getPrefillValue('email') ?? '',
      password: getPrefillValue('password') ?? '',
      password_confirmation: getPrefillValue('password_confirmation') ?? '',
      agree_to_e_sign_agreement: getPrefillValue('agree_to_e_sign_agreement') ?? false,
      agree_to_terms: getPrefillValue('agree_to_terms') ?? false,
    },
    mode: 'onTouched',
  })

  const employerRegistrationMutation = usePostEmployersRegistration({
    mutation: {
      onSuccess: ({ id }) => {
        try {
          window.segment?.identify(id)
          window.posthog?.identify(id, {
            role: 'user',
            email: form.getValues('email')
          })
        } catch {
          // Nothing to do here, we just don't want an analytics error from
          // Segment or PostHog to prevent the user from moving forward.
        }

        navigateToNextStep()
      },
      onError: (error: GrapeAPIErrorType) => {
        if (error.fieldErrors) {
          handleGrapeAPIFieldErrors(error, form)
        } else {
          setError(error)
        }
      },
    },
  })

  /**
   * If the user somehow lands on this page and is already authenticated as an
   * employer admin, redirect them to the basic info page.
   */
  useEffect(() => {
    if (authenticated) {
      navigateToNextStep()
    }
  }, [authenticated])

  function errorFor (field: Parameters<typeof form.register>[0]) {
    const path = field.split('.')

    const error = path.reduce((acc, curr) => {
      // @ts-expect-error: This is too much of a pain to type. Just trust that it's correct!
      return acc && acc[curr]
    }, form.formState.errors) as FieldError | undefined

    return error?.message || null
  }

  function onSubmit () {
    const data = form.getValues()

    employerRegistrationMutation.mutate({
      data: {
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        agree_to_terms: data.agree_to_terms && data.agree_to_e_sign_agreement,
      },
    })
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading="We'll guide you through creating a personalized plan for your team"
        subheading="First, let's create your account so we can save your progress."
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <EmailInput
            {...form.register('email', { required: true })}
            label='Work email address'
            error={errorFor('email')}
            autoFocus
          />
          <PasswordInput
            {...form.register('password', { required: true })}
            label='Password'
            error={errorFor('password')}
          />
          <PasswordInput
            {...form.register('password_confirmation', {
              required: true,
              validate: (value) => {
                return (
                  value === form.getValues('password') ||
                  'Passwords do not match'
                )
              },
            })}
            label='Confirm password'
            error={errorFor('password_confirmation')}
          />
          {errorBanner}
        </CenteredLayout.Container>
        <div className='flex flex-col md:flex-row md:justify-between gap-6 md:gap-4 md:items-end items-center'>
          <div className='flex flex-col gap-4'>
            <Checkbox
              {...form.register('agree_to_e_sign_agreement', { required: true })}
              label={
                <>
                  I agree to the{' '}
                  <TextLink
                    href='https://thatch.com/legal/electronic-communications-consent'
                    target='_blank'
                    rel='noopener'
                  >
                    Electronic Communications Consent
                  </TextLink>
                </>
              }
            />
            <Checkbox
              {...form.register('agree_to_terms', { required: true })}
              label={
                <>
                  I agree to the{' '}
                  <TextLink
                    href='https://thatch.com/legal/privacy-policy'
                    target='_blank'
                    rel='noopener'
                  >
                    Privacy Policy
                  </TextLink>
                  {', '}
                  <TextLink
                    href='https://thatch.com/legal/employer-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Employer Agreement
                  </TextLink>
                  {', and '}
                  <TextLink
                    href='https://thatch.com/legal/financial-partners/banking-services-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Banking Services Agreement
                  </TextLink>
                </>
              }
            />
          </div>
          <ButtonGroup align='center' size='large'>
            <Button
              variant='secondary'
              onClick={navigateToPreviousStep}
            >
              Back
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={
              employerRegistrationMutation.isPending || !form.formState.isValid
            }
            >
              Create account
            </Button>
          </ButtonGroup>
        </div>
      </form>
    </CenteredLayout>
  )
}
